import { formatDimensions } from "@tosize/ui";
import type { IntlShape } from "react-intl";
import type { Shape as ThreeShape } from "three";
import { type RefinementCtx, type ZodTypeAny, z } from "zod";
import type { Input } from "../../../types/shape";
import type { ThreeInputLinesType, ThreeSidePathsType } from "../../../types/three";
import type { DimensionKeys, DimensionProperties } from "../../../types/workpiece";
import { getMaxBasedOnUnit } from "../../utils";
import type InputType from "../Input/InputType";
import { JsonInputType } from "../Input/JsonInputType";
import Shape from "../Shape";
import { SideName } from "../Side/SideName";

export default abstract class Plate extends Shape {
	public calculateSideLength(side: SideName, input: InputType): number | undefined {
		const length = Number(input[JsonInputType.Length]);
		const width = Number(input[JsonInputType.Width]);

		if (!(Number.isNaN(length) || Number.isNaN(width))) {
			switch (side) {
				case SideName.Length1:
				case SideName.Length2:
					return length;
				case SideName.Width1:
				case SideName.Width2:
					return width;
			}
		}

		return undefined;
	}

	public getDimensionsString(input: InputType, intl: IntlShape): string | undefined {
		if (input.length !== undefined && input.width !== undefined) {
			return formatDimensions({
				lang: intl.locale,
				values: [Number(input.length) / 10, Number(input.width) / 10],
			});
		}

		return undefined;
	}

	public getConstraints(
		input: InputType,
		current: Input,
		_equalCorners?: boolean,
	): number | undefined {
		const length = Number(input[JsonInputType.Length] ?? 0);
		const width = Number(input[JsonInputType.Width] ?? 0);

		const panels = this.product?.panelSizes?.filter(
			(panel) => length <= panel.length && width <= panel.width,
		);

		const maxLength = panels?.reduce(
			(prev, current) => (current.saleLength > prev ? current.saleLength : prev),
			0,
		);

		const maxWidth = panels?.reduce(
			(prev, current) => (current.saleWidth > prev ? current.saleWidth : prev),
			0,
		);

		const panelLength = maxLength ?? 0;
		const panelWidth = maxWidth ?? 0;
		const max = current.max ? getMaxBasedOnUnit(current.max, this.unit) : 0;

		switch (current.id) {
			case JsonInputType.Length:
			case JsonInputType.LengthSection1:
			case JsonInputType.LengthSection2:
				return Math.min(max, panelLength);
			case JsonInputType.Width:
			case JsonInputType.WidthSection1:
			case JsonInputType.WidthSection2:
				return Math.min(max, panelWidth);
		}

		return undefined;
	}

	public getThreeShape(_: DimensionProperties[DimensionKeys]): undefined | ThreeShape {
		return undefined;
	}

	public getThreeSidePaths(
		_: DimensionProperties[DimensionKeys],
	): undefined | ThreeSidePathsType[] {
		return undefined;
	}

	public getThreeInputLines(
		_: DimensionProperties[DimensionKeys],
	): undefined | ThreeInputLinesType[] {
		return undefined;
	}

	public getDimensions(
		_: DimensionProperties[DimensionKeys],
	): undefined | DimensionProperties[DimensionKeys] {
		return undefined;
	}

	public calculateSheetDimensions(input: InputType): { length: number; width: number } | undefined {
		const length = Number(input[JsonInputType.Length]);
		const width = Number(input[JsonInputType.Width]);

		if (!(Number.isNaN(length) || Number.isNaN(width))) {
			return { length, width };
		}
	}

	protected getSchemaRefinements(schema: ZodTypeAny): ZodTypeAny {
		const parent = super.getSchemaRefinements(schema);

		if (this.shapeInput.belt_size_constraints !== undefined) {
			return parent.superRefine(this.validateBeltSize.bind(this));
		}

		return parent;
	}

	protected calculateMaxBeltSize(data: Record<JsonInputType, number>) {
		const biggest = Math.max(data.length, data.width);
		const smallest = Math.min(data.length, data.width);

		const maxBeltSize = this.shapeInput.belt_size_constraints?.max_size ?? 0;

		const maxSize = (maxBeltSize - biggest - Number(this.product?.thickness) * 2) / 2;
		const maxSizeInverse = maxBeltSize - smallest * 2 - Number(this.product?.thickness) * 2;

		return { biggest, smallest, maxSize, maxSizeInverse };
	}

	protected validateBeltSize(data: Record<JsonInputType, number>, context: RefinementCtx): void {
		const { biggest, smallest, maxSize } = this.calculateMaxBeltSize(data);

		if (smallest > maxSize) {
			const biggestField = biggest === data.length ? JsonInputType.Length : JsonInputType.Width;
			const smallestField = biggest === data.length ? JsonInputType.Width : JsonInputType.Length;

			context.addIssue({
				code: z.ZodIssueCode.custom,
				params: {
					type: "belt_size",
					field_1: [this.getInputById(smallestField)?.label],
					field_2: [this.getInputById(biggestField)?.label],
					maxValue: maxSize,
					value: biggest,
				},
				path: [smallestField],
			});

			context.addIssue({
				code: z.ZodIssueCode.custom,
				params: {
					type: "belt_size",
					field_1: [this.getInputById(biggestField)?.label],
					field_2: [this.getInputById(smallestField)?.label],
					maxValue: maxSize,
					value: biggest,
				},
				path: [biggestField],
			});
		}
	}
}
